/**
 * bodyScrollLock — блокировка скролла host-страницы, пока открыт модальный слой.
 *
 * Зачем не просто `body { overflow: hidden }` (#1261, ревью): на iOS Safari этот
 * приём **ненадёжен** — страница продолжает скроллиться, особенно если скролл к
 * моменту открытия имел инерцию. Единственное, что держит iOS, — увести body в
 * `position: fixed` со сдвигом на текущий scroll, а при разблокировке вернуть
 * позицию (иначе страница прыгнет наверх). Именно так делают и готовые
 * body-scroll-lock библиотеки; тянуть зависимость ради 30 строк незачем.
 *
 * Счётчик, а не флаг: модальных слоёв может быть несколько (форма поверх
 * лендинга), и внутренний не должен отпускать страницу за внешний. Стили
 * применяются на переходе 0→1 и восстанавливаются на 1→0; сохраняем ИМЕННО
 * прежние inline-значения, поэтому чужой лок (например `overflow: hidden` от
 * оверлея лендинга, #1256) возвращается как был.
 *
 * ВАЖНО: пока лок активен, host-страница в `position: fixed`, то есть её
 * собственные `position: sticky` элементы «замирают». Для модалки это ожидаемо —
 * она перекрывает страницу целиком.
 */

let lockCount = 0;
/** @type {{overflow: string, position: string, top: string, left: string, right: string, width: string, scrollY: number}|null} */
let saved = null;

/** Заблокировать скролл страницы. Идемпотентно по счётчику. */
export function lockBodyScroll() {
  // Guard ПЕРЕД счётчиком: инвариант «счётчик меняется только когда мы реально
  // что-то делаем» должен быть виден сразу, без доказательств (ревью #1261).
  if (typeof document === 'undefined') return;
  lockCount += 1;
  if (lockCount > 1) return; // уже заблокировано внешним слоем

  const { body } = document;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  // Уводя body в `position: fixed`, мы лишаем документ прокрутки, и классический
  // скроллбар (Windows, macOS с «Always show scrollbars») ИСЧЕЗАЕТ — страница
  // дёргается на его ширину при открытии и обратно при закрытии. Компенсируем
  // паддингом. При overlay-скроллбарах ширина 0 и паддинг не ставится.
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

  saved = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    paddingRight: body.style.paddingRight,
    scrollY,
  };

  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  // Ширину НЕ задаём: у fixed-элемента `left: 0; right: 0` уже растягивают его
  // на вьюпорт. `width: 100%` тут был бы не только избыточен, но и завязывал бы
  // результат на `box-sizing` ЧУЖОЙ страницы: при `content-box` паддинг-
  // компенсация ниже добавилась бы к 100% и сама расширила body (ревью #1261).
  body.style.left = '0';
  body.style.right = '0';
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

/** Снять блокировку и вернуть позицию скролла. Идемпотентно по счётчику. */
export function unlockBodyScroll() {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0 || !saved) return;

  const { body } = document;
  body.style.overflow = saved.overflow;
  body.style.position = saved.position;
  body.style.top = saved.top;
  body.style.left = saved.left;
  body.style.right = saved.right;
  body.style.paddingRight = saved.paddingRight;
  // Без этого страница окажется прокрученной наверх: пока body был fixed,
  // документ «потерял» позицию.
  window.scrollTo(0, saved.scrollY);
  saved = null;
}

/** @returns {boolean} активен ли лок. */
export function isBodyScrollLocked() {
  return lockCount > 0;
}

/** Test-only: сбросить состояние модуля между спеками. */
export function __resetBodyScrollLock() {
  lockCount = 0;
  saved = null;
}
